import React, { useState } from 'react'
import { ChevronDown, FolderOpen, EllipsisVertical, Pencil, ArrowUpDown, ExternalLink, Trash2 } from 'lucide-react'
import type { DocumentRecord } from '@/types/catalog'
import { colors } from '@/styles/colors'
import { Checkbox } from '@/components/ui/checkbox'
import { useTranslation } from '@/i18n'
import { getDocumentStatus } from '@/utils/documentStatus'
import { DocumentStatus } from '@/components/StatusTag'
import StatusTag from '@/components/StatusTag'
import { formatFileSize } from '@/utils/fileSize'
import { timeAgo } from '@/utils/timeUtils'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cleanDocumentName } from '@/routes/IntelligentBoard/utils/stripGraphPrefix'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { GroupNameDialog } from './GroupNameDialog'

interface GroupedDocumentRowProps {
  setId: string
  documents: DocumentRecord[]
  onOpenSet?: (documents: DocumentRecord[]) => void
  onRowClick?: (doc: DocumentRecord) => void
  selectedRows?: string[]
  onRowSelect?: (id: string, checked: boolean) => void
  selectable?: boolean
  cols: { id: string; label: string }[]
  onRenameGroup?: (setId: string, newName: string) => void
  onManageFiles?: (setId: string, documents: DocumentRecord[]) => void
  onDeleteGroup?: (setId: string, documents: DocumentRecord[]) => void
  showActions?: boolean
  disabledRows?: string[]
}

export const GroupedDocumentRow: React.FC<GroupedDocumentRowProps> = ({
  setId,
  documents,
  onOpenSet,
  onRowClick,
  selectedRows = [],
  onRowSelect,
  selectable = false,
  // cols is part of the prop type for parent compatibility but not consumed
  // here — the row renders a fixed cell layout; column metadata isn't used.
  onRenameGroup,
  onManageFiles,
  onDeleteGroup,
  showActions = true,
  disabledRows = [],
}) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [showRenameDialog, setShowRenameDialog] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)


  // Sort documents by sheet_index
  const sortedDocs = [...documents].sort((a, b) => {
    const aIndex = a.properties.sheet_index ? parseInt(a.properties.sheet_index, 10) : 0
    const bIndex = b.properties.sheet_index ? parseInt(b.properties.sheet_index, 10) : 0
    return aIndex - bIndex
  })

  // Calculate aggregate values for the group
  const totalSize = sortedDocs.reduce((sum, doc) => sum + (doc.file_size || 0), 0)
  const allStatuses = sortedDocs.map(doc => getDocumentStatus(doc))
  const hasSuccess = allStatuses.some(s => s === DocumentStatus.success)
  const hasProcessing = allStatuses.some(s => s === DocumentStatus.processing || s === DocumentStatus.uploading)
  const groupStatus = hasProcessing ? DocumentStatus.processing : hasSuccess ? DocumentStatus.success : DocumentStatus.failed

  // Get the set name - prefer stored name, fall back to first doc name
  const firstDoc = sortedDocs[0]
  const setName = firstDoc.properties.multi_page_set_name
    || firstDoc.name.split('(')[0].trim()
    || firstDoc.name

  // Get unique uploaders
  const uploaders = Array.from(new Set(sortedDocs.map(doc => doc.uploader_name || doc.user_id).filter(Boolean)))
  const uploaderText = uploaders.length > 1 ? t('data_catalog:multiple_uploaders', 'Multiple') : (uploaders[0] || '')

  // Get earliest upload_started_at (fall back to created_at)
  const earliestDate = sortedDocs.reduce((earliest, doc) => {
    const docDate = new Date(doc.upload_started_at || doc.created_at)
    return !earliest || docDate < earliest ? docDate : earliest
  }, null as Date | null)

  const selectableDocs = sortedDocs.filter(doc => !disabledRows.includes(doc.id))
  const allSelected = selectableDocs.length > 0 && selectableDocs.every(doc => selectedRows.includes(doc.id))
  const someSelected = sortedDocs.some(doc => selectedRows.includes(doc.id))

  const handleCheckboxChange = (checked: boolean) => {
    if (onRowSelect) {
      selectableDocs.forEach(doc => {
        if (checked && !selectedRows.includes(doc.id)) {
          onRowSelect(doc.id, true)
        } else if (!checked && selectedRows.includes(doc.id)) {
          onRowSelect(doc.id, false)
        }
      })
    }
  }

  return (
    <>
      {/* Group Header Row */}
      <tr
        onClick={() => onOpenSet ? onOpenSet(sortedDocs) : setExpanded(!expanded)}
        style={{
          cursor: 'pointer',
          backgroundColor: allSelected || someSelected
            ? 'hsl(var(--muted))' : 'hsl(var(--background))',
          borderBottom: '1px solid hsl(var(--border))',
        }}
        className="group/row hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors"
      >
        {selectable && onRowSelect && (
          <td className="pl-3 p-2" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={allSelected ? true : someSelected ? 'indeterminate' : false}
              onCheckedChange={(checked) => handleCheckboxChange(checked === true)}
            />
          </td>
        )}
        <td className="p-2">
          <div className="flex items-center gap-3 min-w-0">
            <ChevronDown
              className="w-4 h-4 transition-transform flex-shrink-0 cursor-pointer text-gray-500 dark:text-dark-text-muted"
              style={{
                transform: expanded ? 'rotate(180deg)' : 'none',
              }}
              onClick={(e) => {
                e.stopPropagation()
                setExpanded(!expanded)
              }}
            />
            <FolderOpen className="w-4 h-4 flex-shrink-0" style={{ color: colors.purple[600] }} />
            <span className={`font-semibold truncate min-w-0 text-gray-900 dark:text-dark-text ${onOpenSet ? 'group-hover/row:underline' : ''}`}>{setName}</span>
            <span
              className="flex-shrink-0 inline-flex items-center text-gray-500 dark:text-dark-text-muted"
              style={{
                background: 'hsl(var(--muted))',
                fontSize: '12px',
                fontWeight: 500,
                padding: '2px 8px',
                borderRadius: '4px',
                whiteSpace: 'nowrap',
              }}
            >
              {sortedDocs.length} sheets
            </span>
          </div>
        </td>
        <td className="p-2">
          <span>{t(`data_catalog:doc_types.${firstDoc.type}`)}</span>
        </td>
        <td className="p-2">
          {formatFileSize(totalSize)}
        </td>
        <td className="p-2">
          <StatusTag status={groupStatus} />
        </td>
        <td className="p-2">
          <span className="truncate">
            {uploaderText}
          </span>
        </td>
        <td className="p-2">
          {timeAgo(earliestDate?.toISOString() || '')}
        </td>
        {showActions && (
          <td className="p-2 pr-5">
            <div className="w-full flex gap-2">
              <Popover modal={false}>
                <PopoverTrigger asChild>
                  <div
                    className="p-2 flex justify-center items-center bg-gray-100 dark:bg-dark-accent hover:bg-gray-200 dark:hover:bg-dark-muted rounded-full transition-all cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <EllipsisVertical className="w-5 h-5" />
                  </div>
                </PopoverTrigger>
                <PopoverContent className="w-fit rounded-xl" align="start">
                  <div className="w-full flex flex-col gap-2">
                    <div
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowRenameDialog(true)
                      }}
                      className="hover:bg-gray-100 dark:hover:bg-dark-accent cursor-pointer py-2 px-3 rounded-lg flex items-center gap-2"
                    >
                      <Pencil className="w-4 h-4" />
                      <span className="text-sm">{t('data_catalog:graph_editor.multi_page_set.rename_group')}</span>
                    </div>
                    <div
                      onClick={(e) => {
                        e.stopPropagation()
                        onManageFiles?.(setId, sortedDocs)
                      }}
                      className="hover:bg-gray-100 dark:hover:bg-dark-accent cursor-pointer py-2 px-3 rounded-lg flex items-center gap-2"
                    >
                      <ArrowUpDown className="w-4 h-4" />
                      <span className="text-sm">{t('data_catalog:graph_editor.multi_page_set.manage_files')}</span>
                    </div>
                    <div
                      onClick={(e) => {
                        e.stopPropagation()
                        onOpenSet?.(sortedDocs)
                      }}
                      className="hover:bg-gray-100 dark:hover:bg-dark-accent cursor-pointer py-2 px-3 rounded-lg flex items-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span className="text-sm">{t('data_catalog:graph_editor.multi_page_set.open_in_board')}</span>
                    </div>
                    <div className="border-t my-1" />
                    <div
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowDeleteConfirm(true)
                      }}
                      className="hover:bg-gray-100 dark:hover:bg-dark-accent cursor-pointer py-2 px-3 rounded-lg flex items-center gap-2 text-red-600 dark:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="text-sm">{t('data_catalog:graph_editor.multi_page_set.delete_group')}</span>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </td>
        )}
      </tr>

      {/* Expanded Child Rows */}
      {expanded && (
        <>
          {sortedDocs.map((doc, index) => {
            const isDisabled = disabledRows.includes(doc.id)
            // Compute once per row and reuse in the visible name + tooltip.
            const displayName = cleanDocumentName(doc.name, doc.type)
            return (
            <tr
              key={doc.id}
              onClick={() => !isDisabled && onRowClick?.(doc)}
              style={{
                backgroundColor: selectedRows.includes(doc.id)
                  ? 'hsl(var(--muted))' : 'hsl(var(--background))',
                borderBottom: index < sortedDocs.length - 1
                  ? '1px solid hsl(var(--border))' : 'none',
              }}
              className={`hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {selectable && onRowSelect && (
                <td className="pl-3 p-2" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedRows.includes(doc.id)}
                    disabled={isDisabled}
                    onCheckedChange={(checked) => onRowSelect(doc.id, checked === true)}
                  />
                </td>
              )}
              <td className="p-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-2 pl-12 min-w-0">
                      <span className="font-semibold truncate min-w-0">{displayName}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{displayName}</p>
                  </TooltipContent>
                </Tooltip>
              </td>
              <td className="p-2">
                <span>{t(`data_catalog:doc_types.${doc.type}`)}</span>
              </td>
              <td className="p-2">
                {formatFileSize(doc.file_size)}
              </td>
              <td className="p-2">
                <StatusTag status={getDocumentStatus(doc)} />
              </td>
              <td className="p-2">
                <span className="truncate">
                  {doc.uploader_name || doc.user_id}
                </span>
              </td>
              <td className="p-2">
                {timeAgo(doc.upload_started_at || doc.created_at)}
              </td>
              {showActions && <td className="p-2" />}
            </tr>
            )
          })}
        </>
      )}

      {/* Rename Group Dialog */}
      <GroupNameDialog
        isOpen={showRenameDialog}
        onClose={() => setShowRenameDialog(false)}
        onConfirm={(newName) => onRenameGroup?.(setId, newName)}
        defaultName={setName}
        title={t('data_catalog:graph_editor.multi_page_set.rename_group')}
      />

      {/* Delete Group Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('data_catalog:graph_editor.multi_page_set.ungroup_confirm_title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('data_catalog:graph_editor.multi_page_set.ungroup_confirm_description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('common:buttons.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => onDeleteGroup?.(setId, sortedDocs)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('data_catalog:graph_editor.multi_page_set.delete_group')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
